import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import MetricCards from "./MetricCards";
import SovByEngineChart from "./SovByEngineChart";
import SpendAttentionCard from "./SpendAttentionCard";
import { adaptOverview } from "./monitoringAdapters";
import { getMonitoringOverview } from "../../../services/monitoringApi";

export default function MonitoringOverview({ websiteId }) {
	const [data, setData] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);

	useEffect(() => {
		let cancelled = false;
		async function load() {
			if (!websiteId) {
				setLoading(false);
				setError("Missing website");
				return;
			}
			setLoading(true);
			setError(null);
			const res = await getMonitoringOverview(websiteId);
			if (cancelled) return;
			if (!res.ok) {
				setError(res.message || "Failed to load overview");
				setData(null);
				setLoading(false);
				return;
			}
			setData(adaptOverview(res.overview));
			setLoading(false);
		}
		load();
		return () => {
			cancelled = true;
		};
	}, [websiteId]);

	if (loading) {
		return <p className="monitoring__empty text-muted mb-0">Loading overview…</p>;
	}
	if (error) {
		return <p className="monitoring__empty monitoring__empty--error mb-0">{error}</p>;
	}
	if (!data) {
		return <p className="monitoring__empty text-muted mb-0">No overview data yet.</p>;
	}

	const engineKeys = data.engineKeys;
	const sovAll = data.sovScopeKey === "all";

	return (
		<div className="monitoring__overview">
			<div className="monitoring__overview-header">
				<span className="monitoring__overview-scope-pill">
					{data.sovScopeLabel || (sovAll ? "All prompts" : "Unbranded")}
				</span>
				<p className="monitoring__overview-scope-note">
					{sovAll
						? "Metrics measured on all prompts (branded and unbranded)."
						: "Metrics measured on unbranded (competitive) prompts; branded prompts excluded."}{" "}
					Change in{" "}
					<Link
						to="/aivisibility/monitoring/settings"
						className="monitoring__overview-scope-link"
					>
						Settings → Share of Voice scope
					</Link>
					.
				</p>
			</div>
			<MetricCards metrics={data.metrics} />
			<div className="monitoring__detail-grid">
				<SovByEngineChart
					data={data.sovByEngine}
					engineKeys={engineKeys}
					title="Share of voice by engine — recent runs"
					caption={
						data.metrics?.shareOfVoice?.competitorsTracked
							? "Y: share of voice (%) · X: run date."
							: "Y: share of voice (%) · X: run date. Equals your citation rate until competitors are tracked."
					}
				/>
				<SpendAttentionCard spend={data.spend} attention={data.attention} />
			</div>
			<div className="monitoring__detail-grid mt-3">
				<SovByEngineChart
					data={data.citationByEngine}
					engineKeys={engineKeys}
					title="Citation rate by engine — recent runs"
					caption="Y: citation rate (%) · X: run date. Share of engine answers that cite your domain."
				/>
				<SovByEngineChart
					data={data.mentionByEngine}
					engineKeys={engineKeys}
					title="Mention rate by engine — recent runs"
					caption="Y: mention rate (%) · X: run date. Share of engine answers that name your brand."
				/>
			</div>
		</div>
	);
}
